import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Star, Lock, X, ChevronRight } from "lucide-react";
import AppShell from "../components/AppShell";
import { useProfile } from "../context/ProfileContext";
import { REGIONS, GAMES, IMAGES } from "../data/gameData";
import { api } from "../lib/api";

function Stars({ n, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[0, 1, 2].map((i) => (
        <Star key={i} size={size} className={i < n ? "text-[#FFD600]" : "text-white/20"}
              fill={i < n ? "#FFD600" : "transparent"} />
      ))}
    </div>
  );
}

export default function WorldMap() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [regionStars, setRegionStars] = useState({});
  const [active, setActive] = useState(null); // region object

  useEffect(() => {
    if (profile) api.getMastery(profile.id).then((d) => setRegionStars(d.region_stars || {})).catch(() => {});
  }, [profile]);

  return (
    <AppShell>
      <div className="rounded-3xl overflow-hidden relative mb-5 glass">
        <img src={IMAGES.worldMapImg} alt="World Map" className="w-full h-40 sm:h-52 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B1A] via-transparent to-transparent" />
        <div className="absolute bottom-3 left-4">
          <h1 className="text-3xl font-black">World Map</h1>
          <p className="text-xs text-[#A8B2D1]">Pick a land, then choose a game</p>
        </div>
      </div>

      <div className="space-y-3">
        {REGIONS.map((r, i) => {
          const stars = regionStars[r.id] ?? 0;
          const rangeLabel = r.tables ? `Tables ${r.tables[0]}–${r.tables[r.tables.length - 1]}` : "Tables 2–20";
          return (
            <motion.button
              key={r.id}
              data-testid={`region-${r.id}`}
              initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05 * i }}
              onClick={() => setActive(r)}
              className="w-full glass rounded-3xl p-4 flex items-center gap-4 press text-left relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: r.color }} />
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                   style={{ background: `${r.color}22`, boxShadow: `0 0 18px ${r.color}44` }}>
                <span className="font-black text-lg" style={{ color: r.color }}>{i + 1}</span>
              </div>
              <div className="flex-1">
                <div className="font-bold text-lg leading-tight">{r.name}</div>
                <div className="text-[11px] text-[#A8B2D1]">{r.world} · {rangeLabel}</div>
                <div className="mt-1"><Stars n={stars} /></div>
              </div>
              <ChevronRight className="text-[#A8B2D1]" />
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ background: "rgba(5,4,14,0.8)", backdropFilter: "blur(6px)" }}
            onClick={() => setActive(null)} data-testid="region-sheet">
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="glass w-full max-w-lg rounded-3xl p-5">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-2xl font-black" style={{ color: active.color }}>{active.name}</h2>
                <button data-testid="sheet-close" onClick={() => setActive(null)}
                        className="w-9 h-9 rounded-full glass flex items-center justify-center"><X size={18} /></button>
              </div>
              <p className="text-xs text-[#A8B2D1] mb-4">
                {active.world} · {active.tables ? `Tables ${active.tables[0]}–${active.tables[active.tables.length - 1]}` : "All tables 2–20"}
              </p>
              <div className="grid grid-cols-2 gap-3 max-h-[52vh] overflow-y-auto no-scrollbar">
                {GAMES.map((g) => {
                  const Icon = Icons[g.icon] || Star;
                  return (
                    <button
                      key={g.id}
                      data-testid={`sheet-game-${g.id}`}
                      onClick={() => navigate(`/play/${g.id}?region=${active.id}`)}
                      className="press glass rounded-2xl p-3 text-left border border-white/10">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                           style={{ background: `${g.color}22` }}>
                        <Icon size={22} style={{ color: g.color }} />
                      </div>
                      <div className="font-bold text-sm leading-tight">{g.name}</div>
                      <div className="text-[10px] text-[#A8B2D1] leading-tight">{g.tagline}</div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
